/**
 * PDF Export using Web Worker for non-blocking UI
 * Prevents main thread blocking during CPU-intensive PDF generation
 */

// Import the worker
import PDFWorker from "../workers/pdfExport.worker.js?worker";

/**
 * Export project data to PDF using Web Worker
 * @param {Object} project - Project data
 * @param {Array} questions - Array of questions with comments
 * @param {Function} onProgress - Optional callback for progress updates (0-100)
 * @returns {Promise<void>}
 */
export const exportProjectToPDF = async (project, questions, onProgress = null) => {
  return new Promise((resolve, reject) => {
    // Create worker instance
    const worker = new PDFWorker();

    // Set up message handler
    worker.onmessage = (event) => {
      const { type, progress, blob, fileName, error } = event.data;

      switch (type) {
        case "PROGRESS":
          if (onProgress && typeof onProgress === "function") {
            onProgress(progress);
          }
          break;

        case "COMPLETE":
          // Create download link and trigger download
          const url = URL.createObjectURL(blob);
          const link = document.createElement("a");
          link.href = url;
          link.download = fileName;
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          URL.revokeObjectURL(url);

          // Clean up worker
          worker.terminate();
          resolve();
          break;

        case "ERROR":
          // Clean up worker
          worker.terminate();
          reject(new Error(error));
          break;

        default:
          break;
      }
    };

    // Handle worker errors
    worker.onerror = (error) => {
      worker.terminate();
      reject(new Error(`PDF generation failed: ${error.message}`));
    };

    // Send generation request to worker
    worker.postMessage({
      type: "GENERATE_PDF",
      project,
      questions,
    });
  });
};
